import {
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
} from 'react-query';
import { ResponseError } from 'superagent';
import {
  BatchCreateCreditLogsResponse,
  Client,
  ListTeamClientsRequest,
  ListTeamClientsResponse,
} from '@cbinsights/externalapiservice/externalapiservice';
import { request } from 'client/modules/common/utils/request';

export const listTeamClients = (
  req: Partial<ListTeamClientsRequest>
): Promise<ListTeamClientsResponse> =>
  request({
    url: '/service/externalapiservice/ListTeamClients',
    body: req,
  }).then((r) => r.body);

export const useListTeamClients = (idTeam: number) => {
  return useQuery(
    ['externalapiservice/ListTeamClients', idTeam],
    () =>
      listTeamClients({
        id_team: idTeam,
      }),
    { retry: false }
  );
};

export const useGetClient = (
  clientID: string
): UseQueryResult<Client, ResponseError> =>
  useQuery(
    ['externalapiservice/GetClient', clientID],
    () =>
      request({
        url: '/service/externalapiservice/GetClient',
        body: { client_id: clientID },
      })
        .then((res): Client => res.body)
        .catch((error) => {
          // Exclude 404 Not Found
          if (error.status !== 404) throw error;
        }),
    { enabled: clientID !== '' }
  );

const createClientAPI = (client: Client): Promise<Client> =>
  request({
    url: '/service/externalapiservice/CreateClient',
    body: {
      client,
    },
  }).then((res): Client => res.body);

export const useCreateClient = (): UseMutationResult<Client, ResponseError> => {
  return useMutation(createClientAPI);
};

export const useSoftDeleteClient = (client: Client) => {
  return useMutation(() =>
    request({
      url: '/service/externalapiservice/DeleteClient',
      body: { client_id: client.client_id },
    }).then((res): Client => res.body)
  );
};

export const useUndeleteClient = (client: Client) => {
  return useMutation(() =>
    request({
      url: '/service/externalapiservice/UndeleteClient',
      body: { client_id: client.client_id },
    }).then((res): Client => res.body)
  );
};

export const useAddCredits = (): UseMutationResult<
  BatchCreateCreditLogsResponse,
  ResponseError
> => {
  return useMutation(
    ({ clientId, amount }: { clientId: string; amount: number }) =>
      request({
        url: '/service/externalapiservice/BatchCreateCreditLogs',
        body: {
          requests: [
            {
              credit_log: {
                client_id: clientId,
                credit_delta: amount,
                job_service_name_inserted: 'admin-frontend-service',
              },
            },
          ],
        },
      }).then((res): BatchCreateCreditLogsResponse => res.body)
  );
};

type UpdateClientArgs = { client: Partial<Client>; paths: string[] };

const updateClientAPI = ({
  client,
  paths,
}: UpdateClientArgs): Promise<Client> =>
  request({
    url: '/service/externalapiservice/UpdateClient',
    body: {
      client,
      update_mask: { paths },
    },
  }).then((res): Client => res.body);

export const useUpdateClient = (): UseMutationResult<
  Client,
  ResponseError,
  UpdateClientArgs
> => {
  return useMutation(updateClientAPI);
};
